import { useState, useEffect, useCallback, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { AdminLayout } from '@/components/layout/AdminLayout';
import { HotelCard } from '@/components/hotel-search/HotelCard';
import { HotelDetailModal } from '@/components/hotel-search/HotelDetailModal';
import { HotelMap } from '@/components/hotel-search/HotelMap';
import { HotelSearchProgress } from '@/components/hotel-search/HotelSearchProgress';
import { DestinationEnrichmentProgress } from '@/components/hotel-search/DestinationEnrichmentProgress';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { Skeleton } from '@/components/ui/skeleton';
import { useToast } from '@/components/ui/use-toast';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import {
  Calendar, Copy, Check, Hotel, Loader2, Database, SlidersHorizontal, Search, Heart,
} from 'lucide-react';

const HOTEL_SEARCH_BASE_URL = 'https://hotel-search-production.up.railway.app';
const HOTEL_SEARCH_API_KEY = import.meta.env.VITE_HOTEL_SEARCH_API_KEY as string | undefined
  ?? 'VOAH_sk_8fT2xQ!mL9#Kp7@Zr4$Ny1&Wv6^Hd3*Bc8';
import { cn } from '@/lib/utils';
import { supabase } from '@/lib/supabase/client';
import { MOCK_HOTEL_SEARCH, seedMockSearch } from '@/lib/hotel-search-mock';
import {
  HotelSearchData, HotelWithSelection, HotelSelection, HotelStatus, CompetitorQuote,
} from '@/types/hotel-search';
import { normalizeCompetitorQuotes } from '@/lib/hotel-competitor';
import { loadGoogleMapsApi } from '@/lib/google-maps-loader';
import { TooltipProvider } from '@/components/ui/tooltip';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { useHotelSearchProgress } from '@/hooks/useHotelSearchProgress';
import {
  getHeaderMunicipalities,
  getProcessingMunicipalitySet,
} from '@/lib/hotel-search-enrichment';

interface SearchSummary {
  search_id: string;
  created_at: string;
  destinations: string[];
}

function formatDate(dateStr: string) {
  try {
    return format(new Date(dateStr + 'T12:00:00'), "d MMM yyyy", { locale: ptBR });
  } catch {
    return dateStr;
  }
}

function SearchHeaderBar({
  searchData,
  activeMunicipality,
  onMunicipalityChange,
  availableSearches,
  onSearchChange,
  municipalities,
  processingMunicipalities,
}: {
  searchData: HotelSearchData;
  activeMunicipality: string | null;
  onMunicipalityChange: (m: string | null) => void;
  availableSearches: SearchSummary[];
  onSearchChange: (searchId: string) => void;
  municipalities: string[];
  processingMunicipalities: Set<string>;
}) {
  const [copied, setCopied] = useState(false);

  const copySearchId = () => {
    navigator.clipboard.writeText(searchData.search_id);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const activeDest = activeMunicipality
    ? searchData.destinations.find(d => d.municipality === activeMunicipality)
    : null;

  return (
    <div className="bg-white border border-gray-200 rounded-xl px-5 py-3.5 flex items-center gap-4 flex-wrap shadow-sm">

      {/* Search selector */}
      <div className="flex items-center gap-2 min-w-0">
        <Search className="w-3.5 h-3.5 text-gray-400 flex-shrink-0" />
        <Select value={searchData.search_id} onValueChange={onSearchChange}>
          <SelectTrigger className="h-7 text-xs border-none shadow-none bg-transparent px-1 gap-1 min-w-[180px] max-w-[260px] focus:ring-0">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {availableSearches.map(s => (
              <SelectItem key={s.search_id} value={s.search_id}>
                <div className="flex flex-col gap-0.5">
                  <span className="font-mono text-xs text-gray-700 truncate max-w-[220px]">{s.search_id}</span>
                  <span className="text-[10px] text-gray-400">
                    {s.destinations.join(' · ')}
                    {' · '}
                    {format(new Date(s.created_at), "d MMM yyyy", { locale: ptBR })}
                  </span>
                </div>
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <Separator orientation="vertical" className="h-5" />

      {/* Search ID with copy */}
      <div className="flex items-center gap-2 min-w-0">
        <span className="text-xs font-medium text-gray-400 uppercase tracking-wide flex-shrink-0">ID</span>
        <code className="text-sm font-mono text-gray-700 truncate max-w-[200px]">{searchData.search_id}</code>
        <button
          onClick={copySearchId}
          className="text-gray-400 hover:text-voah-teal transition-colors flex-shrink-0"
          title="Copiar ID"
        >
          {copied ? <Check className="w-3.5 h-3.5 text-voah-teal" /> : <Copy className="w-3.5 h-3.5" />}
        </button>
      </div>

      {/* Dates — only when a municipality is selected */}
      {activeDest && activeDest.checkin && activeDest.checkout && (
        <>
          <Separator orientation="vertical" className="h-5" />
          <div className="flex items-center gap-2 text-sm text-gray-600">
            <Calendar className="w-4 h-4 text-gray-400 flex-shrink-0" />
            <span className="font-medium">{formatDate(activeDest.checkin)}</span>
            <span className="text-gray-400">→</span>
            <span className="font-medium">{formatDate(activeDest.checkout)}</span>
            {activeDest.nights && (
              <Badge variant="outline" className="text-xs border-gray-200 text-gray-500 ml-1">
                {activeDest.nights}n
              </Badge>
            )}
          </div>
        </>
      )}

      <Separator orientation="vertical" className="h-5" />

      {/* Municipality filter */}
      <div className="flex items-center gap-1.5">
        <SlidersHorizontal className="w-3.5 h-3.5 text-gray-400 flex-shrink-0" />
        <button
          onClick={() => onMunicipalityChange(null)}
          className={cn(
            'text-xs font-medium px-2.5 py-1 rounded-full transition-all',
            activeMunicipality === null
              ? 'bg-voah-teal text-white'
              : 'text-gray-500 hover:text-gray-700 hover:bg-gray-100',
          )}
        >
          Todos
        </button>
        {municipalities.map(name => {
          const isProcessing = processingMunicipalities.has(name);
          return (
          <button
            key={name}
            onClick={() => onMunicipalityChange(
              activeMunicipality === name ? null : name,
            )}
            className={cn(
              'inline-flex items-center gap-1 text-xs font-medium px-2.5 py-1 rounded-full transition-all',
              activeMunicipality === name
                ? 'bg-voah-teal text-white'
                : isProcessing
                  ? 'text-voah-teal bg-teal-50 border border-teal-200/80 hover:bg-teal-100'
                  : 'text-gray-500 hover:text-gray-700 hover:bg-gray-100',
            )}
          >
            {isProcessing && (
              <Loader2 className={cn(
                'w-3 h-3 flex-shrink-0',
                activeMunicipality === name ? 'animate-spin' : 'animate-spin text-voah-teal',
              )} />
            )}
            {name}
          </button>
          );
        })}
      </div>
    </div>
  );
}

export default function HotelSearchView() {
  const { searchId } = useParams<{ searchId?: string }>();
  const navigate = useNavigate();
  const { toast } = useToast();

  const [searchData, setSearchData] = useState<HotelSearchData | null>(null);
  const [selections, setSelections] = useState<Record<string, HotelSelection>>({});
  const [loading, setLoading] = useState(true);
  const [seeding, setSeeding] = useState(false);
  const [searchStatus, setSearchStatus] = useState<'processing' | 'completed' | 'failed' | null>(null);
  const [activeMunicipality, setActiveMunicipality] = useState<string | null>(null);
  const [filterChosen, setFilterChosen] = useState(false);
  const [filterFavorite, setFilterFavorite] = useState(false);
  const [highlightedHotelId, setHighlightedHotelId] = useState<string | null>(null);
  const [selectedHotel, setSelectedHotel] = useState<HotelWithSelection | null>(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [availableSearches, setAvailableSearches] = useState<SearchSummary[]>([]);

  const targetSearchId = searchId ?? MOCK_HOTEL_SEARCH.search_id;

  // Load the list of all available searches for the selector dropdown
  useEffect(() => {
    async function fetchSearchList() {
      const { data } = await supabase
        .from('hotel_searches')
        .select('search_id, created_at, raw_data')
        .order('created_at', { ascending: false });

      if (data) {
        setAvailableSearches(data.map(row => ({
          search_id: row.search_id,
          created_at: row.created_at,
          destinations: (row.raw_data as HotelSearchData)?.destinations?.map(
            (d: { municipality: string }) => d.municipality,
          ) ?? [],
        })));
      }
    }
    fetchSearchList();
  }, []);

  // Pré-carrega Maps antes do HotelMap montar (após skeleton de loading)
  useEffect(() => {
    if (loading) return;
    loadGoogleMapsApi({ libraries: ['maps'] }).catch(() => { /* HotelMap exibe erro */ });
  }, [loading]);

  // Lock body scroll so only column card lists scroll
  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = ''; };
  }, []);

  // Reset filters when search changes
  useEffect(() => {
    setActiveMunicipality(null);
    setFilterChosen(false);
    setFilterFavorite(false);
  }, [targetSearchId]);

  // Load search data
  const loadSearch = useCallback(async () => {
    setLoading(true);
    try {
      const { data: searchRow, error } = await supabase
        .from('hotel_searches')
        .select('raw_data, status')
        .eq('search_id', targetSearchId)
        .maybeSingle();

      if (error) throw error;

      if (!searchRow) {
        setSearchData(null);
        setSearchStatus(null);
        setLoading(false);
        return;
      }

      const status = (searchRow as { status?: string }).status ?? 'completed';
      setSearchStatus(status as 'processing' | 'completed' | 'failed');

      if (searchRow.raw_data) {
        setSearchData(searchRow.raw_data as HotelSearchData);
      } else if (status !== 'processing') {
        setSearchData(null);
      }

      const { data: selRows } = await supabase
        .from('hotel_selections')
        .select('*')
        .eq('search_id', targetSearchId);

      const selMap: Record<string, HotelSelection> = {};
      (selRows ?? []).forEach(row => {
        selMap[row.hotel_id] = {
          id: row.id,
          search_id: row.search_id,
          hotel_id: row.hotel_id,
          municipality: row.municipality,
          status: row.status as HotelStatus,
          is_favorite: Boolean((row as { is_favorite?: boolean }).is_favorite),
          position: row.position,
          comments: row.comments,
          competitor_quote: normalizeCompetitorQuotes(
            (row as { competitor_quote?: unknown }).competitor_quote,
          ),
        };
      });
      setSelections(selMap);
    } catch (err) {
      console.error('Error loading hotel search:', err);
      toast({ title: 'Erro ao carregar busca', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  }, [targetSearchId, toast]);

  useEffect(() => {
    loadSearch();
  }, [loadSearch]);

  const handleEnrichmentRawData = useCallback((data: HotelSearchData) => {
    setSearchData(data);
  }, []);

  const enrichmentProgress = useHotelSearchProgress(targetSearchId, {
    enabled: searchStatus === 'processing',
    onCompleted: loadSearch,
    onRawDataUpdate: handleEnrichmentRawData,
  });

  const processingMunicipalities = useMemo(
    () => (searchStatus === 'processing'
      ? getProcessingMunicipalitySet(enrichmentProgress, searchData)
      : new Set<string>()),
    [searchStatus, enrichmentProgress, searchData],
  );

  const headerMunicipalities = useMemo(
    () => getHeaderMunicipalities(
      searchData,
      searchStatus === 'processing' ? enrichmentProgress : null,
    ),
    [searchData, searchStatus, enrichmentProgress],
  );

  const displaySearchData = useMemo((): HotelSearchData | null => {
    if (searchData) return searchData;
    if (searchStatus !== 'processing' || headerMunicipalities.length === 0) return null;
    return {
      search_id: targetSearchId,
      generated_at: new Date().toISOString(),
      checkin: '',
      checkout: '',
      nights: 0,
      guests: 2,
      currency: 'BRL',
      destinations: headerMunicipalities.map(municipality => ({
        municipality,
        country: '',
        coordinates: { lat: 0, lng: 0 },
        hotels: [],
      })),
    };
  }, [searchData, searchStatus, targetSearchId, headerMunicipalities]);

  // Derived: all hotels with selection state
  const allHotels = useMemo<HotelWithSelection[]>(() => {
    if (!searchData) return [];
    return searchData.destinations.flatMap(dest =>
      dest.hotels.map(hotel => ({
        ...hotel,
        municipality: dest.municipality,
        selection: selections[hotel.hotel_id] ?? {
          search_id: searchData.search_id,
          hotel_id: hotel.hotel_id,
          municipality: dest.municipality,
          status: 'discarded' as HotelStatus,
          is_favorite: false,
          position: 0,
          comments: null,
          competitor_quote: null,
        },
      })),
    );
  }, [searchData, selections]);

  const municipalityHotels = useMemo(() =>
    activeMunicipality
      ? allHotels.filter(h => h.municipality === activeMunicipality)
      : allHotels,
  [allHotels, activeMunicipality],
  );

  const chosenCount = useMemo(
    () => municipalityHotels.filter(h => h.selection.status === 'chosen').length,
    [municipalityHotels],
  );

  const favoriteCount = useMemo(
    () => municipalityHotels.filter(h => h.selection.is_favorite === true).length,
    [municipalityHotels],
  );

  const matchesSelectionFilters = useCallback((h: HotelWithSelection) => {
    if (!filterChosen && !filterFavorite) return true;
    const isChosen = h.selection.status === 'chosen';
    const isFavorite = h.selection.is_favorite === true;
    if (filterChosen && filterFavorite) return isChosen || isFavorite;
    if (filterChosen) return isChosen;
    return isFavorite;
  }, [filterChosen, filterFavorite]);

  /** Lista e mapa compartilham o mesmo array (município + Escolhidos/Favoritos + ordenação). */
  const visibleHotels = useMemo(() => {
    let list = municipalityHotels.filter(matchesSelectionFilters);
    if (!activeMunicipality && processingMunicipalities.size > 0) {
      list = list.filter(h => !processingMunicipalities.has(h.municipality));
    }
    list.sort((a, b) => {
      const dp = (a.selection.position ?? 0) - (b.selection.position ?? 0);
      if (dp !== 0) return dp;
      return a.hotel_id.localeCompare(b.hotel_id);
    });
    return list;
  }, [municipalityHotels, matchesSelectionFilters, activeMunicipality, processingMunicipalities]);

  const activeMunicipalityProcessing = activeMunicipality != null
    && processingMunicipalities.has(activeMunicipality);

  const backgroundProcessingMunicipality = useMemo(() => {
    if (searchStatus !== 'processing' || activeMunicipalityProcessing) return null;
    const list = [...processingMunicipalities];
    return list.length >= 1 ? list[0] : null;
  }, [searchStatus, activeMunicipalityProcessing, processingMunicipalities]);

  /**
   * Chave só para fitBounds — muda quando entra/sai hotel visível (município/filtro),
   * não quando escolher/favoritar altera apenas o estado de seleção.
   */
  const mapBoundsKey = useMemo(() => {
    if (!searchData) return '';

    const hotels = searchData.destinations.flatMap(dest =>
      dest.hotels.map(hotel => ({
        id: hotel.hotel_id,
        municipality: dest.municipality,
      })),
    );

    let ids = activeMunicipality
      ? hotels.filter(h => h.municipality === activeMunicipality).map(h => h.id)
      : hotels.map(h => h.id);

    if (filterChosen || filterFavorite) {
      ids = ids.filter(id => {
        const sel = selections[id];
        const isChosen = sel?.status === 'chosen';
        const isFavorite = sel?.is_favorite === true;
        if (filterChosen && filterFavorite) return isChosen || isFavorite;
        if (filterChosen) return isChosen;
        return isFavorite;
      });
    }

    return ids.slice().sort().join('\0');
  }, [
    searchData,
    activeMunicipality,
    filterChosen,
    filterFavorite,
    filterChosen || filterFavorite ? selections : null,
  ]);

  /** Noites por município — para exibir contexto do período ao lado do preço */
  const nightsMap = useMemo<Record<string, number>>(() => {
    if (!searchData) return {};
    const map: Record<string, number> = {};
    for (const dest of searchData.destinations) {
      if (dest.nights) map[dest.municipality] = dest.nights;
    }
    return map;
  }, [searchData]);

  // Números do mapa / badge: mesma ordem da listagem acima (1..n); independente de `chosen`
  const mapIndexMap = useMemo<Record<string, number>>(() => {
    const map: Record<string, number> = {};
    visibleHotels.forEach((h, i) => { map[h.hotel_id] = i + 1; });
    return map;
  }, [visibleHotels]);

  const handleMarkerClick = useCallback((hotelId: string) => {
    setHighlightedHotelId(hotelId);
    requestAnimationFrame(() => {
      document.getElementById(`hotel-card-${hotelId}`)?.scrollIntoView({
        behavior: 'smooth',
        block: 'nearest',
      });
    });
  }, []);


  const handleToggleChosen = useCallback(async (hotelId: string) => {
    if (!searchData) return;
    const hotel = allHotels.find(h => h.hotel_id === hotelId);
    if (!hotel) return;

    /** Apenas alterna `status`; mantém `position` para não reordenar nem renumerar lista/mapa. */
    let updatedSel: HotelSelection | undefined;
    setSelections(prev => {
      const existing = prev[hotelId];
      const currentStatus = existing?.status ?? hotel.selection.status;
      const nextStatus: HotelStatus = currentStatus === 'chosen' ? 'discarded' : 'chosen';
      const nextPosition = existing?.position ?? hotel.selection.position ?? 0;

      const next: HotelSelection = {
        ...(existing ?? {
          search_id: searchData.search_id,
          hotel_id: hotelId,
          municipality: hotel.municipality,
          position: 0,
          comments: hotel.selection.comments ?? null,
          competitor_quote: hotel.selection.competitor_quote ?? null,
        }),
        status: nextStatus,
        position: nextPosition,
      };
      updatedSel = next;
      return { ...prev, [hotelId]: next };
    });

    if (!updatedSel) return;

    if (selectedHotel?.hotel_id === hotelId) {
      setSelectedHotel(prev =>
        prev ? { ...prev, selection: updatedSel } : null);
    }

    try {
      if (updatedSel.id) {
        await supabase
          .from('hotel_selections')
          .update({ status: updatedSel.status, position: updatedSel.position })
          .eq('id', updatedSel.id);
      } else {
        const { data } = await supabase
          .from('hotel_selections')
          .insert({
            search_id: searchData.search_id,
            hotel_id: hotelId,
            municipality: hotel.municipality,
            status: updatedSel.status,
            position: updatedSel.position,
            comments: hotel.selection.comments ?? null,
            competitor_quote: hotel.selection.competitor_quote ?? null,
          })
          .select()
          .single();

        if (data) {
          setSelections(prev => ({
            ...prev,
            [hotelId]: { ...prev[hotelId], id: data.id },
          }));
          if (selectedHotel?.hotel_id === hotelId) {
            setSelectedHotel(prev =>
              prev ? { ...prev, selection: { ...prev.selection, id: data.id } } : null);
          }
        }
      }
    } catch (err) {
      console.error('Error updating choice:', err);
      toast({ title: 'Erro ao salvar escolha', variant: 'destructive' });
    }
  }, [searchData, allHotels, selectedHotel, toast]);

  const handleToggleFavorite = useCallback(async (hotelId: string) => {
    if (!searchData) return;
    const hotel = allHotels.find(h => h.hotel_id === hotelId);
    if (!hotel) return;

    let updatedSel: HotelSelection | undefined;
    setSelections(prev => {
      const existing = prev[hotelId];
      const nextFavorite = !(existing?.is_favorite ?? hotel.selection.is_favorite);

      const next: HotelSelection = {
        ...(existing ?? {
          search_id: searchData.search_id,
          hotel_id: hotelId,
          municipality: hotel.municipality,
          status: hotel.selection.status,
          position: hotel.selection.position ?? 0,
          comments: hotel.selection.comments ?? null,
          competitor_quote: hotel.selection.competitor_quote ?? null,
        }),
        is_favorite: nextFavorite,
      };
      updatedSel = next;
      return { ...prev, [hotelId]: next };
    });

    if (!updatedSel) return;

    if (selectedHotel?.hotel_id === hotelId) {
      setSelectedHotel(prev =>
        prev ? { ...prev, selection: updatedSel } : null);
    }

    try {
      if (updatedSel.id) {
        await supabase
          .from('hotel_selections')
          .update({ is_favorite: updatedSel.is_favorite })
          .eq('id', updatedSel.id);
      } else {
        const { data } = await supabase
          .from('hotel_selections')
          .insert({
            search_id: searchData.search_id,
            hotel_id: hotelId,
            municipality: hotel.municipality,
            status: updatedSel.status,
            is_favorite: updatedSel.is_favorite,
            position: updatedSel.position,
            comments: hotel.selection.comments ?? null,
            competitor_quote: hotel.selection.competitor_quote ?? null,
          })
          .select()
          .single();

        if (data) {
          setSelections(prev => ({
            ...prev,
            [hotelId]: { ...prev[hotelId], id: data.id },
          }));
          if (selectedHotel?.hotel_id === hotelId) {
            setSelectedHotel(prev =>
              prev ? { ...prev, selection: { ...prev.selection, id: data.id } } : null);
          }
        }
      }
    } catch (err) {
      console.error('Error updating favorite:', err);
      toast({ title: 'Erro ao salvar favorito', variant: 'destructive' });
    }
  }, [searchData, allHotels, selectedHotel, toast]);

  const handleCommentSave = useCallback(async (hotelId: string, comment: string) => {
    setSelections(prev => ({
      ...prev,
      [hotelId]: { ...prev[hotelId], comments: comment },
    }));

    if (selectedHotel?.hotel_id === hotelId) {
      setSelectedHotel(prev =>
        prev ? { ...prev, selection: { ...prev.selection, comments: comment } } : null);
    }

    try {
      const existing = selections[hotelId];
      if (existing?.id) {
        await supabase.from('hotel_selections').update({ comments: comment }).eq('id', existing.id);
      }
    } catch (err) {
      console.error('Error saving comment:', err);
    }

    toast({ title: 'Comentário salvo', description: 'Observação registrada com sucesso.' });
  }, [selections, selectedHotel, toast]);

  const handleCompetitorQuoteSave = useCallback(async (hotelId: string, quotes: CompetitorQuote[] | null) => {
    if (!searchData) return;
    const hotel = allHotels.find(h => h.hotel_id === hotelId);
    if (!hotel) return;

    const payloadQuote = quotes?.length ? quotes : null;

    setSelections(prev => ({
      ...prev,
      [hotelId]: { ...prev[hotelId], competitor_quote: payloadQuote },
    }));

    if (selectedHotel?.hotel_id === hotelId) {
      setSelectedHotel(prev =>
        prev ? { ...prev, selection: { ...prev.selection, competitor_quote: payloadQuote } } : null);
    }

    try {
      const existing = selections[hotelId];
      const payload = { competitor_quote: payloadQuote };
      if (existing?.id) {
        await supabase.from('hotel_selections').update(payload).eq('id', existing.id);
      } else {
        const { data } = await supabase
          .from('hotel_selections')
          .insert({
            search_id: searchData.search_id,
            hotel_id: hotelId,
            municipality: hotel.municipality,
            status: hotel.selection.status,
            position: hotel.selection.position,
            comments: hotel.selection.comments ?? null,
            competitor_quote: payloadQuote,
          })
          .select()
          .single();
        if (data) {
          setSelections(prev => ({
            ...prev,
            [hotelId]: { ...prev[hotelId], id: data.id },
          }));
        }
      }
      toast({
        title: 'Cotações salvas',
        description: payloadQuote?.length
          ? `${payloadQuote.length} preço(ns) de outro(s) fornecedor(es) registrado(s).`
          : 'Comparativos removidos.',
      });
    } catch (err) {
      console.error('Error saving competitor quote:', err);
      toast({ title: 'Erro ao salvar cotações', variant: 'destructive' });
    }
  }, [searchData, selections, selectedHotel, allHotels, toast]);

  const handleOpenDetail = useCallback((hotel: HotelWithSelection) => {
    setSelectedHotel(hotel);
    setModalOpen(true);
  }, []);

  const handleRefreshHotelUrl = useCallback(async (hotelId: string, newUrl: string) => {
    if (!searchData) return;

    const hotel = allHotels.find(h => h.hotel_id === hotelId);
    if (!hotel) return;

    const dest = searchData.destinations.find(d => d.municipality === hotel.municipality);
    if (!dest) return;

    // Validate that required URL fields are present before sending
    const googleMapsUrl = hotel.google_maps_url?.trim() || null;
    if (!googleMapsUrl) {
      toast({
        title: 'URL do Google Maps ausente',
        description: 'Este hotel não possui uma URL do Google Maps válida no cadastro.',
        variant: 'destructive',
      });
      throw new Error('google_maps_url missing');
    }

    // Build hotel entry — only include optional fields when they have valid values
    const hotelEntry: Record<string, unknown> = {
      hotel_id: hotel.hotel_id,
      booking_url: newUrl,
      google_maps_url: googleMapsUrl,
      coordinates: hotel.coordinates,
      keywords: hotel.keywords ?? [],
    };
    if (hotel.google_reviews?.rating != null) hotelEntry.google_reviews = hotel.google_reviews;
    if (hotel.walking_distance) hotelEntry.walking_distance = hotel.walking_distance;

    const destEntry: Record<string, unknown> = {
      municipality: dest.municipality,
      country: dest.country,
      coordinates: dest.coordinates,
      hotels: [hotelEntry],
    };
    if (dest.checkin) destEntry.checkin = dest.checkin;
    if (dest.checkout) destEntry.checkout = dest.checkout;

    const payload = {
      search_id: searchData.search_id,
      on_conflict: 'refresh',
      checkin: searchData.checkin,
      checkout: searchData.checkout,
      guests: searchData.guests,
      currency: searchData.currency,
      lang: 'pt-br',
      destinations: [destEntry],
    };

    console.debug('[hotel-refresh] payload →', JSON.stringify(payload, null, 2));

    const response = await fetch(`${HOTEL_SEARCH_BASE_URL}/api/hotel-search-ingest`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${HOTEL_SEARCH_API_KEY}`,
      },
      body: JSON.stringify(payload),
    });

    if (!response.ok) {
      const body = await response.text().catch(() => '');
      toast({ title: 'Erro ao re-enriquecer hotel', description: body || `HTTP ${response.status}`, variant: 'destructive' });
      throw new Error(`HTTP ${response.status}`);
    }

    // Poll status until the enrichment completes (max ~90s)
    const maxAttempts = 30;
    for (let i = 0; i < maxAttempts; i++) {
      await new Promise(resolve => setTimeout(resolve, 3000));
      try {
        const statusRes = await fetch(
          `${HOTEL_SEARCH_BASE_URL}/api/hotel-search-status/${searchData.search_id}`,
          { headers: { 'Authorization': `Bearer ${HOTEL_SEARCH_API_KEY}` } },
        );
        if (statusRes.ok) {
          const statusData = await statusRes.json() as { status: string };
          if (statusData.status === 'completed' || statusData.status === 'failed') break;
        }
      } catch { /* ignore transient errors */ }
    }

    await loadSearch();
    toast({ title: 'Hotel atualizado', description: 'Dados re-enriquecidos com a nova URL do Booking.' });
  }, [searchData, allHotels, loadSearch, toast]);

  const handleSeedDemo = async () => {
    setSeeding(true);
    const id = await seedMockSearch();
    if (id) {
      await loadSearch();
      toast({ title: 'Dados de demonstração carregados!' });
    } else {
      toast({ title: 'Erro ao carregar demonstração', variant: 'destructive' });
    }
    setSeeding(false);
  };

  // Empty state — only when not processing and no data
  if (!loading && !displaySearchData && searchStatus !== 'processing') {
    return (
      <AdminLayout title="Seleção de Hotéis" hideSidebar onBack={() => navigate('/admin')}>
        <div className="flex flex-col items-center justify-center min-h-[60vh] gap-6">
          <div className="w-16 h-16 rounded-2xl bg-voah-gray flex items-center justify-center">
            <Hotel className="w-8 h-8 text-voah-teal" />
          </div>
          <div className="text-center">
            <h2 className="text-xl font-bold text-gray-800 mb-1">Nenhuma busca encontrada</h2>
            <p className="text-sm text-gray-500">Carregue os dados de demonstração para explorar a interface.</p>
          </div>
          <Button
            onClick={handleSeedDemo}
            disabled={seeding}
            className="bg-voah-teal hover:bg-voah-teal-dark text-white gap-2"
          >
            {seeding ? <Loader2 className="w-4 h-4 animate-spin" /> : <Database className="w-4 h-4" />}
            Carregar demonstração (Japão 2026)
          </Button>
        </div>
      </AdminLayout>
    );
  }

  // Loading state
  if (loading) {
    return (
      <AdminLayout title="Seleção de Hotéis" hideSidebar onBack={() => navigate('/admin')}>
        <div className="space-y-4">
          <Skeleton className="h-14 w-full rounded-xl" />
          <div className="flex gap-4">
            <div className="flex-1 basis-0 min-w-0 space-y-3">
              <Skeleton className="h-10 w-full rounded-xl" />
              {[1, 2, 3].map(j => <Skeleton key={j} className="h-72 w-full rounded-xl" />)}
            </div>
            <Skeleton className="flex-1 basis-0 min-w-0 min-h-[200px] rounded-xl" />
          </div>
        </div>
      </AdminLayout>
    );
  }

  if (!loading && !displaySearchData && searchStatus === 'processing') {
    return (
      <AdminLayout title="Seleção de Hotéis" hideSidebar onBack={() => navigate('/admin')}>
        <div
          className="flex flex-col"
          style={{ position: 'fixed', top: '64px', left: 0, right: 0, bottom: 0 }}
        >
          <HotelSearchProgress searchId={targetSearchId} onCompleted={loadSearch} />
        </div>
      </AdminLayout>
    );
  }

  if (!loading && !displaySearchData) {
    return null;
  }

  return (
    <TooltipProvider delayDuration={300}>
      <AdminLayout title="Seleção de Hotéis" hideSidebar onBack={() => navigate('/admin')}>
        <div
          className="flex flex-col px-6"
          style={{
            position: 'fixed',
            top: '64px',
            left: 0,
            right: 0,
            bottom: 0,
          }}
        >
          <div className="pt-2 pb-3 flex-shrink-0">
            <SearchHeaderBar
              searchData={displaySearchData!}
              activeMunicipality={activeMunicipality}
              onMunicipalityChange={setActiveMunicipality}
              availableSearches={availableSearches}
              onSearchChange={(id) => navigate(`/admin/hotel-search/${id}`)}
              municipalities={headerMunicipalities}
              processingMunicipalities={processingMunicipalities}
            />
          </div>

          <div className="flex gap-4 flex-1 min-h-0 pb-3">

            <div className="flex flex-col rounded-xl border border-teal-200/80 bg-teal-50/30 flex-1 basis-0 min-w-0 min-h-0 overflow-hidden">
              <div className="flex items-center justify-between gap-2 px-4 py-3 border-b border-teal-200/60 bg-white/60 flex-shrink-0 flex-wrap">
                <div className="flex items-center gap-2">
                  <Hotel className="w-4 h-4 text-voah-teal" />
                  <span className="text-sm font-semibold text-voah-teal">Hotéis</span>
                </div>
                <div className="flex items-center gap-1.5 flex-wrap">
                  <button
                    type="button"
                    onClick={() => setFilterChosen(v => !v)}
                    className={cn(
                      'inline-flex items-center gap-1 text-xs font-medium px-2.5 py-1 rounded-full transition-all',
                      filterChosen
                        ? 'bg-voah-teal text-white'
                        : 'text-gray-600 bg-white/90 border border-gray-200 hover:border-voah-teal/40 hover:text-voah-teal',
                    )}
                  >
                    <Check className="w-3 h-3" strokeWidth={2.5} />
                    Escolhidos
                    <span className={cn(
                      'tabular-nums',
                      filterChosen ? 'text-white/80' : 'text-gray-400',
                    )}>
                      ({chosenCount})
                    </span>
                  </button>
                  <button
                    type="button"
                    onClick={() => setFilterFavorite(v => !v)}
                    className={cn(
                      'inline-flex items-center gap-1 text-xs font-medium px-2.5 py-1 rounded-full transition-all',
                      filterFavorite
                        ? 'bg-rose-500 text-white'
                        : 'text-gray-600 bg-white/90 border border-gray-200 hover:border-rose-300 hover:text-rose-600',
                    )}
                  >
                    <Heart className={cn('w-3 h-3', filterFavorite && 'fill-current')} />
                    Favoritos
                    <span className={cn(
                      'tabular-nums',
                      filterFavorite ? 'text-white/80' : 'text-gray-400',
                    )}>
                      ({favoriteCount})
                    </span>
                  </button>
                  <Badge variant="secondary" className="text-xs bg-white/90 text-gray-600 tabular-nums">
                    {visibleHotels.length}
                  </Badge>
                </div>
              </div>
              {searchStatus === 'processing' && backgroundProcessingMunicipality && (
                <DestinationEnrichmentProgress
                  municipality={backgroundProcessingMunicipality}
                  progress={enrichmentProgress}
                  compact
                />
              )}
              <div className="flex-1 overflow-y-auto p-3 space-y-3 min-h-0">
                {activeMunicipalityProcessing ? (
                  <DestinationEnrichmentProgress
                    municipality={activeMunicipality!}
                    progress={enrichmentProgress}
                  />
                ) : (
                  <>
                {visibleHotels.map(hotel => (
                  <HotelCard
                    key={hotel.hotel_id}
                    hotel={hotel}
                    mapIndex={mapIndexMap[hotel.hotel_id]}
                    nights={nightsMap[hotel.municipality]}
                    isHighlighted={highlightedHotelId === hotel.hotel_id}
                    onOpenDetail={handleOpenDetail}
                    onMouseEnter={() => setHighlightedHotelId(hotel.hotel_id)}
                    onMouseLeave={() => setHighlightedHotelId(null)}
                    onToggleChosen={handleToggleChosen}
                    onToggleFavorite={handleToggleFavorite}
                    onRefreshBookingUrl={handleRefreshHotelUrl}
                  />
                ))}
                {visibleHotels.length === 0 && !activeMunicipalityProcessing && (
                  <div className="flex flex-col items-center justify-center py-12 text-gray-400 text-sm text-center px-4">
                    {filterChosen || filterFavorite
                      ? 'Nenhum hotel corresponde aos filtros de seleção.'
                      : searchStatus === 'processing' && processingMunicipalities.size > 0
                        ? 'Destinos prontos aparecem aqui. Selecione um município ou aguarde o enriquecimento.'
                        : 'Nenhum hotel neste filtro.'}
                  </div>
                )}
                  </>
                )}
              </div>
            </div>

            <div className="min-h-0 h-full flex-1 basis-0 min-w-0">
              <HotelMap
                hotels={visibleHotels}
                destinations={displaySearchData!.destinations}
                activeMunicipality={activeMunicipality}
                boundsKey={mapBoundsKey}
                highlightedHotelId={highlightedHotelId}
                nightsByMunicipality={nightsMap}
                onMarkerClick={handleMarkerClick}
              />
            </div>
          </div>
        </div>

        <HotelDetailModal
          hotel={selectedHotel}
          open={modalOpen}
          nights={selectedHotel ? nightsMap[selectedHotel.municipality] : undefined}
          onClose={() => setModalOpen(false)}
          onToggleChosen={handleToggleChosen}
          onToggleFavorite={handleToggleFavorite}
          onCommentSave={handleCommentSave}
          onCompetitorQuoteSave={handleCompetitorQuoteSave}
        />
      </AdminLayout>
    </TooltipProvider>
  );
}
